import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { subscribeCompanyConversations, isUnread } from '@/services/conversationService'
import { useUserAuthStore } from '@/stores/userAuthStore'
import { logger } from '@/services/loggerService'

/**
 * Client-side conversations for the current company.
 * One listener shared by the Conversations page, the floating widget and the nav badge.
 */
export const useConversationsStore = defineStore('conversations', () => {
  const userAuthStore = useUserAuthStore()

  const companyId = ref(null)
  const conversations = ref([])
  const loading = ref(false)
  const error = ref(null)
  const isWidgetOpen = ref(false)
  let unsubscribe = null

  const unreadCount = computed(() => {
    const uid = userAuthStore.user?.id
    if (!uid) return 0
    return conversations.value.filter((c) => isUnread(c, uid, false)).length
  })

  function start(id) {
    if (!id) return
    if (unsubscribe && companyId.value === id) return
    stop()
    companyId.value = id
    loading.value = true
    error.value = null
    unsubscribe = subscribeCompanyConversations(
      id,
      (list) => {
        conversations.value = list
        loading.value = false
      },
      (err) => {
        error.value = err
        loading.value = false
      }
    )
    logger.info('[ConversationsStore] Started', { companyId: id })
  }

  function stop() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    companyId.value = null
    conversations.value = []
    loading.value = false
    isWidgetOpen.value = false
  }

  function getById(id) {
    return conversations.value.find((c) => c.id === id) || null
  }

  function toggleWidget(open) {
    isWidgetOpen.value = typeof open === 'boolean' ? open : !isWidgetOpen.value
  }

  return {
    companyId,
    conversations,
    loading,
    error,
    isWidgetOpen,
    unreadCount,
    start,
    stop,
    getById,
    toggleWidget
  }
})
